// InvestorPLWaterfall.js — CHF waterfall: principal -> SWIFT -> FX fee -> yield -> WHT -> net.
// Hedged and unhedged columns with optional close-now overlay.

import React, { useState, useMemo } from 'react';
import { DollarSign, Eye, EyeOff } from 'lucide-react';

function fmtCHF(value) {
  if (value == null) return '--';
  const num = parseFloat(value);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  return `${sign}CHF ${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function colorClass(value) {
  if (value == null) return 'text-zinc-500';
  const num = parseFloat(value);
  if (num > 0) return 'text-emerald-600';
  if (num < 0) return 'text-red-600';
  return 'text-zinc-700';
}

const WATERFALL_LINES = [
  { key: 'principal', label: 'Principal Invested', sign: 1, type: 'neutral' },
  { key: 'swift_fee', label: 'SWIFT Transfer Fee', sign: -1, type: 'cost' },
  { key: 'fx_conversion_fee', label: 'FX Conversion Fee', sign: -1, type: 'cost' },
  { key: 'gross_yield', label: 'Gross Yield (KES, Converted)', sign: 1, type: 'income' },
  { key: 'wht', label: 'Withholding Tax (15%)', sign: -1, type: 'cost' },
  { key: 'hedge_cost', label: 'Forward Hedge Cost', sign: -1, type: 'cost' },
  { key: 'fx_gain_loss', label: 'FX Gain / Loss', sign: 1, type: 'variable' },
  { key: 'net', label: 'Net Return (CHF)', sign: 1, type: 'total' },
];

export default function InvestorPLWaterfall({ experiment, latestMark, closeNowData }) {
  const [showCloseNow, setShowCloseNow] = useState(false);

  // Build hedged waterfall values from experiment + mark data
  const hedgedValues = useMemo(() => {
    if (!experiment) return {};
    const mark = latestMark || {};
    return {
      principal: experiment.investment_chf,
      swift_fee: experiment.swift_fee_chf,
      fx_conversion_fee: experiment.fx_fee_chf,
      gross_yield: mark.accrued_yield_chf ?? experiment.projected_yield_chf,
      wht: mark.wht_chf ?? experiment.projected_wht_chf,
      hedge_cost: mark.hedge_cost_chf ?? experiment.hedge_cost_chf,
      fx_gain_loss: 0, // Hedged: FX gain/loss is neutralised
      net: mark.net_return_hedged_chf ?? experiment.projected_net_hedged_chf,
    };
  }, [experiment, latestMark]);

  // Build unhedged waterfall values
  const unhedgedValues = useMemo(() => {
    if (!experiment) return {};
    const mark = latestMark || {};
    return {
      principal: experiment.investment_chf,
      swift_fee: experiment.swift_fee_chf,
      fx_conversion_fee: experiment.fx_fee_chf,
      gross_yield: mark.accrued_yield_chf ?? experiment.projected_yield_chf,
      wht: mark.wht_chf ?? experiment.projected_wht_chf,
      hedge_cost: 0, // No hedge
      fx_gain_loss: mark.fx_gain_loss_chf ?? 0,
      net: mark.net_return_unhedged_chf ?? experiment.projected_net_unhedged_chf,
    };
  }, [experiment, latestMark]);

  // Close-now overlay values
  const closeNowValues = useMemo(() => {
    if (!closeNowData) return null;
    return {
      principal: closeNowData.principal_chf,
      swift_fee: closeNowData.swift_fee_chf,
      fx_conversion_fee: closeNowData.fx_fee_chf,
      gross_yield: closeNowData.accrued_yield_chf,
      wht: closeNowData.wht_chf,
      hedge_cost: closeNowData.hedge_unwind_chf,
      fx_gain_loss: closeNowData.fx_gain_loss_chf,
      net: closeNowData.net_chf,
    };
  }, [closeNowData]);

  if (!experiment) {
    return (
      <div className="bg-white border border-zinc-200 rounded-none p-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Investor P&L Waterfall
        </span>
        <p className="font-mono text-sm text-zinc-400 mt-3">
          No experiment data available.
        </p>
      </div>
    );
  }

  const displayCloseNow = showCloseNow && closeNowValues;

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4" style={{ color: '#4D4D4D' }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Investor P&L Waterfall (CHF)
          </span>
        </div>
        {closeNowData && (
          <button
            onClick={() => setShowCloseNow(!showCloseNow)}
            className="flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] border border-zinc-200 hover:bg-zinc-50 transition-colors"
            style={{ color: '#4D4D4D' }}
          >
            {showCloseNow ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showCloseNow ? 'Hide Close-Now' : 'Show Close-Now'}
          </button>
        )}
      </div>

      {/* Waterfall table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-zinc-200">
              <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                Line Item
              </th>
              <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                Hedged
              </th>
              <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                Unhedged
              </th>
              {displayCloseNow && (
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-600 text-right py-2 px-2 font-medium">
                  Close Now
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {WATERFALL_LINES.map(line => {
              const hedged = hedgedValues[line.key];
              const unhedged = unhedgedValues[line.key];
              const closeNow = displayCloseNow ? closeNowValues[line.key] : null;
              const isTotal = line.type === 'total';
              const displayHedged = line.sign < 0 && hedged != null ? -Math.abs(parseFloat(hedged || 0)) : hedged;
              const displayUnhedged = line.sign < 0 && unhedged != null ? -Math.abs(parseFloat(unhedged || 0)) : unhedged;
              const displayCloseNowVal = displayCloseNow && line.sign < 0 && closeNow != null
                ? -Math.abs(parseFloat(closeNow || 0)) : closeNow;

              return (
                <tr
                  key={line.key}
                  className={`border-b ${
                    isTotal ? 'border-zinc-300 bg-zinc-50' : 'border-zinc-100'
                  }`}
                >
                  <td className={`py-2 px-2 font-mono text-xs ${
                    isTotal ? 'font-semibold text-zinc-900' : 'text-zinc-700'
                  }`}>
                    {line.label}
                  </td>
                  <td className={`py-2 px-2 text-right font-mono tabular-nums ${
                    isTotal ? 'font-semibold' : ''
                  } ${colorClass(displayHedged)}`}>
                    {fmtCHF(displayHedged)}
                  </td>
                  <td className={`py-2 px-2 text-right font-mono tabular-nums ${
                    isTotal ? 'font-semibold' : ''
                  } ${colorClass(displayUnhedged)}`}>
                    {fmtCHF(displayUnhedged)}
                  </td>
                  {displayCloseNow && (
                    <td className={`py-2 px-2 text-right font-mono tabular-nums ${
                      isTotal ? 'font-semibold' : ''
                    } ${colorClass(displayCloseNowVal)}`}>
                      {fmtCHF(displayCloseNowVal)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Net return summary */}
      <div className="grid grid-cols-2 gap-3 mt-4 border-t border-zinc-200 pt-3">
        <div className="border border-zinc-200 p-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Net (Hedged)
          </p>
          <p className={`font-mono text-lg font-semibold tabular-nums ${colorClass(hedgedValues.net)}`}>
            {fmtCHF(hedgedValues.net)}
          </p>
        </div>
        <div className="border border-zinc-200 p-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Net (Unhedged)
          </p>
          <p className={`font-mono text-lg font-semibold tabular-nums ${colorClass(unhedgedValues.net)}`}>
            {fmtCHF(unhedgedValues.net)}
          </p>
        </div>
      </div>
    </div>
  );
}
